/**
 * 指数概览（V1 第一行，2026-09-16 拍板：**永久保留**）
 *
 * 三个图照 V1 的口径与外观搬过来：
 *  · 指数仪表盘 —— `/healthHabit/index`，窗口是"上周"、只算运动维（原口径，见 window.resolveLastWeekWindow）；
 *  · 运动时段偏好饼图 —— 四段（上午/下午/晚上/夜间）与配色沿用 V1；
 *  · 近 6 周实际 vs 目标折线 —— 同样来自那个接口的 scores/targets。
 *
 * 图表怎么来的：**复用页面上已经加载的全局 `window.echarts`**（.umirc.ts 的 scripts 里那条 CDN，
 * V1 一直这么用）。零新增依赖、零打包体积 —— 这就是"最小粒度引入图表"的答案；
 * 拿不到 echarts（未加载/单测环境）时退化成文字，不报错也不白屏。
 *
 * 与 V1 的唯一一处不同：折线上缺失的那一周画成**断点**，不补 0。
 * V1 把空值当 0（P8 记录在案的病灶），那样会把"这周没记录"说成"这周是 0"；外形一致，但不复制这个 bug。
 *
 * 本文件不出现任何中文串：图上的字全部来自 copy.js 的 OVERVIEW_COPY（buildChecks 会扫）。
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Card, Spin } from 'antd';
import { getHealthHabitIndex } from '@/services/healthHabit';
import { resolveLastWeekWindow } from '../window';
import { numericOf } from './series';
import { formatNumber, OVERVIEW_COPY } from './copy';
import styles from './index.less';

// 与 V1 一致：仪表盘的量程与主色
const DIAL_MIN = 1;
const DIAL_MAX = 1.3;
const COLOR_DIAL = '#06b6d4';
const COLOR_ACTUAL = '#13c2c2';
const COLOR_BASELINE = '#94a3b8';
const COLOR_INK = '#0f172a';
const COLOR_SOFT = '#64748b';

const HEIGHT = { dial: 168, pie: 196, line: 196 };

const chartsReady = () =>
  typeof window !== 'undefined' && Boolean(window.echarts);

/** 仪表盘（V1 的 EIndexDial：量程 1.0–1.3，中心写服务端给的档位描述，下面写系数） */
function dialOption(coefficient, levelDesc) {
  const raw = Number(coefficient);
  const value = Number.isFinite(raw)
    ? Math.min(DIAL_MAX, Math.max(DIAL_MIN, raw))
    : DIAL_MIN;
  return {
    tooltip: { show: false },
    series: [
      {
        type: 'gauge',
        min: DIAL_MIN,
        max: DIAL_MAX,
        startAngle: 210,
        endAngle: -30,
        radius: '90%',
        axisLine: { lineStyle: { width: 12, color: [[1, '#e6eef5']] } },
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        progress: {
          show: true,
          roundCap: true,
          width: 12,
          itemStyle: { color: COLOR_DIAL },
        },
        detail: {
          valueAnimation: true,
          formatter: () => String(levelDesc || ''),
          color: COLOR_INK,
          fontSize: 22,
          offsetCenter: [0, 0],
        },
        data: [{ value }],
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '62%',
        style: {
          text: OVERVIEW_COPY.dialCoefficient(value.toFixed(1)),
          fill: COLOR_SOFT,
          fontSize: 12,
        },
      },
    ],
  };
}

/** 时段偏好饼图：四段的文案与颜色来自 copy.js（产品语义，不是随手选的） */
function prefOption(preferences) {
  const byKey = {};
  (Array.isArray(preferences) ? preferences : []).forEach((item) => {
    if (item && item.key) byKey[item.key] = numericOf(item) || 0;
  });
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
    legend: { show: false },
    series: [
      {
        type: 'pie',
        radius: ['40%', '65%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
        label: {
          show: true,
          formatter: '{b}\n{d}%',
          color: COLOR_SOFT,
          fontSize: 11,
        },
        labelLine: { show: true, length: 8, length2: 8 },
        data: OVERVIEW_COPY.prefSlots.map((slot) => ({
          value: byKey[slot.key] || 0,
          name: slot.label,
          itemStyle: { color: slot.color },
        })),
      },
    ],
  };
}

/** 近 6 周实际 vs 目标：缺失的周留 null（断点），不补 0 */
function weekOption(scores, targets) {
  const list = Array.isArray(targets) ? targets : [];
  const labels = list.map((item) => (item && item.key) || '');
  const byKey = {};
  (Array.isArray(scores) ? scores : []).forEach((item) => {
    if (item && item.key) byKey[item.key] = item;
  });
  const pick = (item) =>
    item && item.observed === true ? numericOf(item) : null;
  // 2026-09-16 拍板：“不存在的结果用 0 补充”（V1 原口径）。
  // 与 V2 别处“缺失不补 0”的做法相反 —— 这里是产品明确要的 V1 第一行外观，仅此一处。
  const pick0 = (item) => {
    const value = pick(item);
    return value === null ? 0 : value;
  };
  return {
    grid: { top: 28, right: 20, bottom: 24, left: 44, containLabel: true },
    tooltip: { trigger: 'axis' },
    legend: {
      top: 0,
      icon: 'circle',
      textStyle: { color: COLOR_SOFT, fontSize: 11 },
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e6eef5' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: COLOR_SOFT, fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: { type: 'dashed', color: 'rgba(148,163,184,0.35)' },
      },
    },
    series: [
      {
        name: OVERVIEW_COPY.actualName,
        type: 'line',
        smooth: true,
        connectNulls: true,
        data: labels.map((label) => pick0(byKey[label])),
        lineStyle: { width: 3, color: COLOR_ACTUAL },
        // 实际值画成面积图（2026-09-16 拍板）
        // 颜色写死 rgba，不用 echarts.graphic.LinearGradient：那个构造函数要在构建 option 时就能拿到
        // window.echarts，而拿不到库时（单测/CDN 未加载）会直接抛错 —— 图可以退化，页面不能挂。
        areaStyle: { color: 'rgba(19,194,194,0.18)' },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: COLOR_ACTUAL,
          borderColor: '#fff',
          borderWidth: 2,
        },
      },
      {
        name: OVERVIEW_COPY.targetName,
        type: 'line',
        smooth: true,
        data: list.map(pick),
        lineStyle: { width: 2, color: COLOR_BASELINE, type: 'dashed' },
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: { color: COLOR_BASELINE },
      },
    ],
  };
}

/** echarts 生命周期（init / setOption / resize / dispose），三个图共用 */
function useEcharts(option) {
  const ref = React.useRef(null);
  const chart = React.useRef(null);
  React.useEffect(() => {
    const echarts = typeof window !== 'undefined' ? window.echarts : null;
    if (!echarts || !ref.current || !option) return undefined;
    if (!chart.current) chart.current = echarts.init(ref.current);
    chart.current.setOption(option, true);
    const resize = () => {
      if (chart.current) chart.current.resize();
    };
    window.addEventListener('resize', resize);
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(resize);
      observer.observe(ref.current);
    }
    return () => {
      window.removeEventListener('resize', resize);
      if (observer) observer.disconnect();
      if (chart.current) {
        chart.current.dispose();
        chart.current = null;
      }
    };
  }, [option]);
  return ref;
}

function ChartCard({ title, option, height, fallback }) {
  const ref = useEcharts(option);
  return (
    <Card size="small" className={styles.overviewCard} title={title}>
      {chartsReady() ? (
        <div style={{ height, width: '100%' }} ref={ref} />
      ) : (
        <div className={styles.overviewFallback} style={{ height }}>
          {fallback}
        </div>
      )}
    </Card>
  );
}

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  option: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  height: PropTypes.number.isRequired,
  fallback: PropTypes.node,
};

ChartCard.defaultProps = { option: null, fallback: null };

export default function OverviewRow({ userId, preferences, windowDays }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // 上周口径也是"进页面时定一次"：翻页/重渲染不该换锚点
  const week = React.useMemo(() => resolveLastWeekWindow(), []);

  React.useEffect(() => {
    let mounted = true;
    setData(null);
    setLoading(true);
    // 单测里这个服务可能没被 mock 全：拿不到就退化成文字，而不是把整页炸掉
    if (typeof getHealthHabitIndex !== 'function') {
      setLoading(false);
      return undefined;
    }
    Promise.resolve(
      getHealthHabitIndex({
        requestData: {
          userId,
          periodStart: week.periodStart,
          periodEnd: week.periodEnd,
          dimension: 'SPORT',
        },
      }),
    )
      .then((res) => {
        if (mounted) setData((res && res.data) || null);
      })
      .catch(() => {
        if (mounted) setData(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [userId, week.periodStart, week.periodEnd]);

  const coefficient = data ? numericOf(data.habitCoefficient) : null;
  const levelDesc = (data && data.habitLevelDesc) || '';
  const dial = React.useMemo(
    () => dialOption(coefficient, levelDesc),
    [coefficient, levelDesc],
  );
  const pref = React.useMemo(() => prefOption(preferences), [preferences]);
  const weekTrend = React.useMemo(
    () => weekOption(data && data.scores, data && data.targets),
    [data],
  );

  const pending = loading ? <Spin size="small" /> : null;
  const dialFallback = pending || (
    <div>
      <div className={styles.overviewValue}>
        {coefficient === null
          ? OVERVIEW_COPY.noData
          : formatNumber(coefficient)}
      </div>
      {levelDesc ? (
        <div className={styles.overviewHint}>{levelDesc}</div>
      ) : null}
    </div>
  );

  return (
    <div className={styles.overview} data-test="hh-v2-overview">
      <ChartCard
        title={OVERVIEW_COPY.dialTitle}
        option={dial}
        height={HEIGHT.dial}
        fallback={dialFallback}
      />
      <ChartCard
        title={OVERVIEW_COPY.prefTitle(windowDays)}
        option={pref}
        height={HEIGHT.pie}
        fallback={pending || OVERVIEW_COPY.chartUnavailable}
      />
      <ChartCard
        title={OVERVIEW_COPY.weekTitle}
        option={weekTrend}
        height={HEIGHT.line}
        fallback={pending || OVERVIEW_COPY.chartUnavailable}
      />
    </div>
  );
}

OverviewRow.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // indicators 里的 sport.activePreferences（{key,value} 数组）；缺失就画空饼，不报错
  preferences: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  windowDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OverviewRow.defaultProps = { preferences: [], windowDays: '' };
